import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { toImg } from "@/api/client";

/**
 * Fullscreen lightbox gallery with prev/next.
 * Props:
 *  - images: array of URLs
 *  - openIndex: number | null
 *  - onClose: () => void
 */
export default function Lightbox({ images = [], openIndex, onClose, alt = "" }) {
  const [idx, setIdx] = useState(openIndex ?? 0);

  useEffect(() => {
    if (openIndex != null) setIdx(openIndex);
  }, [openIndex]);

  useEffect(() => {
    if (openIndex == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, images.length, onClose]);

  if (openIndex == null || images.length === 0) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[70] bg-stone-950/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <X className="h-5 w-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
            aria-label="Anterior"
            data-testid="lightbox-prev"
            className="absolute left-5 md:left-10 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
            aria-label="Seguinte"
            data-testid="lightbox-next"
            className="absolute right-5 md:right-10 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <img
        src={toImg(images[idx])}
        alt={alt ? `${alt} — foto ${idx + 1} de ${images.length}` : `Foto ${idx + 1} de ${images.length}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
      />

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm">
        {idx + 1} / {images.length}
      </div>
    </div>
  );
}
